import { interactionPromptSchema } from '@app/schemas/interaction-prompt';
import z from 'zod';
import { actionSchema } from '@app/schemas/action';
import { textActivatableTranslatableSchema, textTranslatableSchema } from '@app/schemas/text';
import { imageActivatableTranslatableSchema, imageTranslatableSchema } from '@app/schemas/image';
import { colorActivatableTranslatableSchema, colorTranslatableSchema } from '@app/schemas/color';
import { sizeActivatableTranslatableSchema, sizeTranslatableSchema } from '@app/schemas/size';

export const navigationTabBarImageSchema = z.object({
  id: z.uuid(),
  type: z.literal('image'),
  name: z.string(),
  image: imageTranslatableSchema,
  theme: z.object({
    width: sizeTranslatableSchema.optional(),
    background: z.object({
      color: colorTranslatableSchema.optional(),
    }).optional(),
    border: z.object({
      radius: sizeTranslatableSchema.optional(),
    }).optional(),
  }).optional(),
});

z.globalRegistry.add(navigationTabBarImageSchema, { id: 'navigation-tab-bar-image' });

export type NavigationTabBarImageSchema = z.infer<typeof navigationTabBarImageSchema>;

export const navigationTabBarButtonSchema = z.object({
  id: z.uuid(),
  type: z.literal('button'),
  action: actionSchema,
  label: textActivatableTranslatableSchema,
  icon: imageActivatableTranslatableSchema.optional(),
  theme: z.object({
    width: sizeActivatableTranslatableSchema.optional(),
    background: z.object({
      color: colorActivatableTranslatableSchema.optional(),
    }).optional(),
    foreground: z.object({
      color: colorActivatableTranslatableSchema.optional(),
    }).optional(),
    border: z.object({
      color: colorActivatableTranslatableSchema.optional(),
      radius: sizeActivatableTranslatableSchema.optional(),
    }).optional(),
  }).optional(),
});

z.globalRegistry.add(navigationTabBarButtonSchema, { id: 'navigation-tab-bar-button' });

export type NavigationTabBarButtonSchema = z.infer<typeof navigationTabBarButtonSchema>;

export const navigationTabBarImageButtonSchema = z.object({
  id: z.uuid(),
  type: z.literal('image-button'),
  action: actionSchema,
  name: z.string(),
  image: imageActivatableTranslatableSchema,
  theme: z.object({
    width: sizeActivatableTranslatableSchema.optional(),
    background: z.object({
      color: colorActivatableTranslatableSchema.optional(),
    }).optional(),
    border: z.object({
      color: colorActivatableTranslatableSchema.optional(),
      radius: sizeActivatableTranslatableSchema.optional(),
    }).optional(),
  }).optional(),
});

z.globalRegistry.add(navigationTabBarImageButtonSchema, { id: 'navigation-tab-bar-image-button' });

export type NavigationTabBarImageButtonSchema = z.infer<typeof navigationTabBarImageButtonSchema>;

export const navigationTabBarItemSchema = z.discriminatedUnion('type', [
  navigationTabBarImageSchema,
  navigationTabBarButtonSchema,
  navigationTabBarImageButtonSchema,
]);

z.globalRegistry.add(navigationTabBarItemSchema, { id: 'navigation-tab-bar-item' });

export type NavigationTabBarItemSchema = z.infer<typeof navigationTabBarItemSchema>;

export const idleTapActionSchema = z.strictObject({
  type: z.literal('page'),
  pageId: z.uuid({ error: 'Choose a page to open on idle tap.' }),
});

z.globalRegistry.add(idleTapActionSchema, { id: 'navigation-idle-tap-action' });

interface IdlePageAvailability {
  enabled?: boolean;
  schedule?: readonly unknown[];
  languages?: readonly string[];
  happyHour?: unknown;
}

/** Eligibility is independent of the current time, locale, or Happy Hour status. */
export function isAlwaysAvailablePage(page: { availability?: IdlePageAvailability }): boolean {
  const availability = page.availability;
  return availability?.enabled !== false && !availability?.schedule?.length
    && !availability?.languages?.length && availability?.happyHour === undefined;
}

export function getIdlePageIssue(
  pages: readonly { id: string; availability?: IdlePageAvailability }[] | undefined,
  navigation: { tabBar?: { enabled?: boolean }; idleTapAction?: { type: 'page'; pageId: string } },
): string | undefined {
  if (navigation.tabBar?.enabled !== false) return undefined;
  if (!navigation.idleTapAction) return 'Choose an Always-available page for idle taps when the tab bar is hidden.';
  const page = pages?.find(candidate => candidate.id === navigation.idleTapAction?.pageId);
  if (!page) return 'The idle tap page no longer exists.';
  if (!isAlwaysAvailablePage(page)) return 'The idle tap page must be enabled and always available.';
  return undefined;
}

export const navigationSchema = z.object({
  backButton: z.object({
    label: textTranslatableSchema,
  }),
  tabBar: z.object({
    enabled: z.boolean().optional(),
    hiddenWhenIdle: z.boolean(),
    items: z.array(navigationTabBarItemSchema),
  }),
  idleTapAction: idleTapActionSchema.optional(),
  interactionPrompt: interactionPromptSchema.optional(),
}).superRefine((navigation, context) => {
  if (navigation.tabBar.enabled === false && !navigation.idleTapAction) {
    context.addIssue({
      code: 'custom', path: ['idleTapAction', 'pageId'],
      message: 'Choose an Always-available page for idle taps when the tab bar is hidden.',
    });
  }
});

z.globalRegistry.add(navigationSchema, { id: 'navigation' });

export type NavigationSchema = z.infer<typeof navigationSchema>;
